import { Box, CircularProgress } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { ImageCarouselType } from '../../../models/ContentManagementInterface';
import BackendService from '../../../services/admin/BackendService';
import { newContentId, newTextContent } from '../../../services/TemplateUtils';
import type { VideoMetadata } from '../../../services/admin/adminBackendServiceVideoLibraryMixin';
import type { CarouselItemContent } from '../../template/imageCarouselTypes';
import AddSlideCard from './AddSlideCard';
import BlockFootnotesEditor from './BlockFootnotesEditor';
import ImageCardsFromScreenshot from './ImageCardsFromScreenshot';
import ImageCarouselSlideCard from './ImageCarouselSlideCard';
import InlineSectionHeader from './InlineSectionHeader';
import { ImageCardsCropMeta, useImageCardsSplit } from './useImageCardsSplit';

interface ImageCarouselBlockEditorProps {
  block: ImageCarouselType;
  onChange: (props: ImageCarouselType['properties']) => void;
  modelId: string;
  modelType?: string;
  fundOpportunityId?: string;
  backendService: BackendService;
  /**
   * Set once, right after a type-mismatch confirm swapped a different block
   * into this (now-empty) Image Cards block — runs it through the exact same
   * paste pipeline a direct paste would (`split.offerOrAdd`), so crop review
   * and "Adjust crops" behave identically either way.
   */
  autoImportFile?: File;
  onAutoImportConsumed?: () => void;
  /**
   * Set once, right after an unscoped Documents-tab import appended this
   * block already populated with detected cards — primes "Adjust crops"
   * without re-applying the (already-current) properties.
   */
  primeCardsAdjust?: ImageCardsCropMeta;
  onPrimeCardsAdjustConsumed?: () => void;
  /** Shows the Footnotes panel — toggled from a button in this block's toolbar strip. */
  footnotesOpen?: boolean;
}

export default function ImageCarouselBlockEditor({
  block,
  onChange,
  modelId,
  modelType = 'FundOpportunity',
  fundOpportunityId,
  backendService,
  autoImportFile,
  onAutoImportConsumed,
  primeCardsAdjust,
  onPrimeCardsAdjustConsumed,
  footnotesOpen = false,
}: ImageCarouselBlockEditorProps) {
  const { properties } = block;
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const [addingSlide, setAddingSlide] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const propertiesRef = useRef(properties);
  propertiesRef.current = properties;

  const updateItem = (index: number, field: 'heading' | 'text', value: string) => {
    const items = [...properties.items];
    if (field === 'heading') {
      items[index] = {
        ...items[index],
        heading: { ...items[index].heading, content: value },
      };
    } else {
      const existing = items[index].text;
      items[index] = {
        ...items[index],
        text: { id: existing?.id ?? newContentId(), content: value },
      };
    }
    onChange({ ...properties, items });
  };

  const updateSlide = (index: number, patch: Partial<CarouselItemContent>) => {
    const items = [...properties.items];
    items[index] = { ...items[index], ...patch };
    onChange({ ...properties, items });
  };

  const handleToggleVideo = (index: number, isVideo: boolean) => {
    updateSlide(index, { isVideo });
  };

  const handleVideoURLChange = (index: number, url: string) => {
    const item = properties.items[index];
    updateSlide(index, {
      isVideo: true,
      videoSource: 'external',
      videoURL: { id: item.videoURL?.id ?? newContentId(), url },
      libraryVideoExternalId: undefined,
    });
  };

  const handleLibraryVideoAttached = (index: number, externalId: string) => {
    const item = properties.items[index];
    updateSlide(index, {
      id: item.id ?? newContentId(),
      isVideo: true,
      videoSource: 'library',
      libraryVideoExternalId: externalId,
      videoURL: undefined,
    });
  };

  const handleImageFile = async (index: number, file: File) => {
    setUploadingIndex(index);
    try {
      const ext = file.name.split('.').pop() ?? 'jpg';
      const url = await backendService.uploadAssetContent(
        'images',
        modelId,
        modelType,
        file.name,
        file,
        ext,
        true,
      );
      const items = [...properties.items];
      items[index] = { ...items[index], imageUrl: { id: newContentId(), url } };
      onChange({ ...properties, items });
    } catch (err) {
      console.error('Image upload failed:', err);
    } finally {
      setUploadingIndex(null);
    }
  };

  /** A new slide is created only by giving the placeholder card an image or a video link. */
  const createSlideFromImage = async (file: File) => {
    setAddingSlide(true);
    try {
      const ext = file.name.split('.').pop() ?? 'jpg';
      const url = await backendService.uploadAssetContent(
        'images',
        modelId,
        modelType,
        file.name,
        file,
        ext,
        true,
      );
      onChange({
        ...properties,
        items: [
          ...properties.items,
          {
            imageUrl: { id: newContentId(), url },
            heading: newTextContent(''),
            text: newTextContent(''),
          },
        ],
      });
    } catch (err) {
      console.error('Image upload failed:', err);
    } finally {
      setAddingSlide(false);
    }
  };

  const createSlideFromVideoUrl = (url: string) => {
    onChange({
      ...properties,
      items: [
        ...properties.items,
        {
          isVideo: true,
          videoSource: 'external',
          videoURL: { id: newContentId(), url },
          heading: newTextContent(''),
          text: newTextContent(''),
        },
      ],
    });
  };

  // Reads the latest properties via the ref: a video upload can outlast edits made meanwhile.
  const createSlideFromLibraryVideo = (externalId: string) => {
    const latest = propertiesRef.current;
    onChange({
      ...latest,
      items: [
        ...latest.items,
        {
          id: newContentId(),
          isVideo: true,
          videoSource: 'library',
          libraryVideoExternalId: externalId,
          heading: newTextContent(''),
          text: newTextContent(''),
        },
      ],
    });
  };

  /** Adds the file to the video library, then makes a slide from it (which shows compression progress). */
  const createSlideFromVideoFile = async (file: File, metadata: VideoMetadata) => {
    setAddingSlide(true);
    setAddError(null);
    try {
      const uploaded = await backendService.uploadVideoToLibrary(metadata, modelId, file);
      createSlideFromLibraryVideo(uploaded.externalId);
    } catch (err) {
      setAddError(
        (err as { error?: string; message?: string })?.error ??
          (err as Error)?.message ??
          'Video upload failed.',
      );
    } finally {
      setAddingSlide(false);
    }
  };

  const removeSlide = (index: number) => {
    onChange({
      ...properties,
      items: properties.items.filter((_, itemIndex) => itemIndex !== index),
    });
  };

  const split = useImageCardsSplit<ImageCarouselType['properties']>({
    hasSlides: properties.items.length > 0,
    modelId,
    modelType,
    contentBlockId: block.id,
    backendService,
    onApply: onChange,
    onAddSlide: createSlideFromImage,
  });

  // A type-mismatch confirm just swapped a different block into this
  // (now-empty) Image Cards block — run the file through the exact same
  // pipeline a direct paste would. Guarded so a re-render with the same
  // file (e.g. React StrictMode's double-invoke) doesn't re-fire.
  const importedFileRef = useRef<File | null>(null);
  useEffect(() => {
    if (!autoImportFile || importedFileRef.current === autoImportFile) return;
    importedFileRef.current = autoImportFile;
    split.offerOrAdd(autoImportFile);
    onAutoImportConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoImportFile]);

  // An unscoped Documents-tab import appended this block already populated
  // with detected cards — prime "Adjust crops" without re-applying the
  // (already-current) properties. Same re-render guard as autoImportFile.
  const primedCardsRef = useRef<ImageCardsCropMeta | null>(null);
  useEffect(() => {
    if (
      !primeCardsAdjust ||
      !primeCardsAdjust.source ||
      primedCardsRef.current === primeCardsAdjust
    ) {
      return;
    }
    primedCardsRef.current = primeCardsAdjust;
    split.primeAdjust(propertiesRef.current, primeCardsAdjust.source, primeCardsAdjust.boxes);
    onPrimeCardsAdjustConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primeCardsAdjust]);

  const busy = split.busy || uploadingIndex !== null || addingSlide;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, position: 'relative' }}>
      {busy && (
        <Box
          data-testid="carousel-busy-overlay"
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 20,
            bgcolor: 'rgba(255,255,255,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      )}

      <InlineSectionHeader
        heading={properties.heading}
        onChange={(heading) => onChange({ ...properties, heading })}
      />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {properties.items.map((item, index) => (
          <ImageCarouselSlideCard
            key={index}
            index={index}
            item={item}
            uploading={uploadingIndex === index}
            onFile={(file) => handleImageFile(index, file)}
            onRemove={() => removeSlide(index)}
            onUpdateHeading={(value) => updateItem(index, 'heading', value)}
            onUpdateText={(value) => updateItem(index, 'text', value)}
            fundOpportunityId={fundOpportunityId ?? modelId}
            backendService={backendService}
            onToggleVideo={(isVideo) => handleToggleVideo(index, isVideo)}
            onVideoURLChange={(url) => handleVideoURLChange(index, url)}
            onLibraryVideoAttached={(externalId) => handleLibraryVideoAttached(index, externalId)}
          />
        ))}

        <AddSlideCard
          adding={addingSlide}
          onFile={createSlideFromImage}
          onVideoUrl={createSlideFromVideoUrl}
          onLibraryVideo={createSlideFromLibraryVideo}
          onVideoFile={createSlideFromVideoFile}
          error={addError}
        />
      </Box>

      {footnotesOpen && (
        <BlockFootnotesEditor
          footnotes={properties.footnotes}
          onChange={(footnotes) => onChange({ ...properties, footnotes })}
        />
      )}
      <ImageCardsFromScreenshot split={split} />
    </Box>
  );
}
